export type Patch = {
    readonly stream: MediaStreamAudioDestinationNode
    readonly sourceStreamId: string
    readonly targetDeviceId: string
    readonly targetChannel: number
    source?: MediaStreamAudioSourceNode
    splitter?: ChannelSplitterNode
}

type Device = {
    readonly context: AudioContext
    readonly merger: ChannelMergerNode
}

export class WebAudioPatcher {
    private devices: Map<string, Device> = new Map()
    private _patches: Map<string, Patch> = new Map()

    public addPatch(patch: Patch) {
        if (this._patches.has(patch.sourceStreamId)) {
            throw new Error(
                `stream ${patch.sourceStreamId} already has a patch. A stream may only be the source for one patch.`
            )
        }
        const device = this.getDevice(patch.targetDeviceId)

        if (patch.targetChannel + patch.stream.channelCount - 1 >= device.merger.numberOfInputs) {
            throw new Error(
                `Cannot map stream with ${patch.stream.channelCount} channels to device with ${device.merger.numberOfInputs} output channels at position ${patch.targetChannel}`
            )
        }

        patch.splitter = device.context.createChannelSplitter(patch.stream.channelCount)
        patch.splitter.channelInterpretation = 'discrete'

        patch.source = device.context.createMediaStreamSource(patch.stream.stream)

        patch.source.connect(patch.splitter)
        for (let i = 0; i < patch.stream.channelCount; i++) {
            patch.splitter.connect(device.merger, i, patch.targetChannel + i)
        }

        this._patches.set(patch.sourceStreamId, patch)
    }

    public addPatchFromTrack(track: MediaStreamTrack, targetDeviceId: string, targetChannel: number) {
        const device = this.getDevice(targetDeviceId)

        const dest = device.context.createMediaStreamDestination()
        dest.channelCount = track.getSettings().channelCount ?? 1
        dest.channelInterpretation = 'discrete'

        const src = device.context.createMediaStreamSource(new MediaStream([track]))
        src.connect(dest)

        const patch: Patch = {
            stream: dest,
            sourceStreamId: track.id,
            targetDeviceId,
            targetChannel,
        }

        this.addPatch(patch)
    }

    public removePatch(sourceStreamId: string) {
        const patch = this._patches.get(sourceStreamId)
        if (patch) {
            patch.stream.disconnect()
            patch.splitter?.disconnect()
            patch.source?.disconnect()
            this._patches.delete(sourceStreamId)
        }
        // TODO: Check for unused audio contexts and remove them
    }

    public getMaxOutputChannels(deviceId: string) {
        const device = this.getDevice(deviceId)
        return device.context.destination.maxChannelCount ?? 0
    }

    private getDevice(deviceId: string): Device {
        if (!this.devices.has(deviceId)) {
            /* @ts-ignore sinkId is a valid option */
            const context = new AudioContext({ sinkId: deviceId })
            context.destination.channelInterpretation = 'discrete'
            context.destination.channelCount = context.destination.maxChannelCount ?? 2
            const merger = context.createChannelMerger(context.destination.maxChannelCount ?? 2)
            merger.channelInterpretation = 'discrete'
            merger.connect(context.destination)
            this.devices.set(deviceId, { context, merger })
        }
        return this.devices.get(deviceId)!
    }

    get patches(): Map<string, Patch> {
        return this._patches
    }
}

export const patcher = new WebAudioPatcher()

/* @ts-ignore expose patcher on window for debugging*/
window.audioPatcher = patcher
